import type {
  TenantSessionRecapCompletedDrill,
  TenantSessionRecapDrillOption,
  TenantSessionRecapPayload,
  TenantSessionRecapResponse,
} from '@tark/shared/types';

export interface SessionRecapDraft {
  completedDrills: TenantSessionRecapCompletedDrill[];
  customDrillLabel: string;
  progressNote: string;
  noteExpanded: boolean;
  homeworkExpanded: boolean;
  homeworkEnabled: boolean;
  homeworkTitle: string;
  homeworkDueAt: string;
  shareWithFamily: boolean;
  hadHomework: boolean;
}

export function createSessionRecapDraft(recap: TenantSessionRecapResponse): SessionRecapDraft {
  return {
    completedDrills: recap.completedDrills ?? [],
    customDrillLabel: '',
    progressNote: recap.progressNote ?? '',
    noteExpanded: Boolean(recap.progressNote),
    homeworkExpanded: Boolean(recap.homework),
    homeworkEnabled: Boolean(recap.homework),
    homeworkTitle: recap.homework?.title ?? '',
    homeworkDueAt: toLocalDateTimeInput(recap.homework?.dueAt),
    shareWithFamily: recap.homework?.shareWithFamily ?? true,
    hadHomework: Boolean(recap.homework),
  };
}

export function toggleRecapDrill(
  draft: SessionRecapDraft,
  option: TenantSessionRecapDrillOption
): SessionRecapDraft {
  const selected = draft.completedDrills.some((item) => item.drillId === option.drillId);
  return {
    ...draft,
    completedDrills: selected
      ? draft.completedDrills.filter((item) => item.drillId !== option.drillId)
      : [...draft.completedDrills, { drillId: option.drillId, label: option.label, done: true }],
  };
}

export function addCustomRecapDrill(draft: SessionRecapDraft): SessionRecapDraft {
  const label = draft.customDrillLabel.trim();
  if (!label) return draft;
  const duplicate = draft.completedDrills.some(
    (item) => !item.drillId && item.label.trim().toLowerCase() === label.toLowerCase()
  );
  return {
    ...draft,
    customDrillLabel: '',
    completedDrills: duplicate
      ? draft.completedDrills
      : [...draft.completedDrills, { drillId: null, label, done: true }],
  };
}

export function buildSessionRecapPayload(draft: SessionRecapDraft): TenantSessionRecapPayload {
  const title = draft.homeworkTitle.trim();
  return {
    drills: draft.completedDrills.map((item) => ({ ...item, done: true })),
    progressNote: draft.progressNote.trim() || null,
    homework: draft.homeworkEnabled && title
      ? {
          title,
          dueAt: draft.homeworkDueAt ? new Date(draft.homeworkDueAt).toISOString() : null,
          shareWithFamily: draft.shareWithFamily,
        }
      : null,
    removeHomework: draft.hadHomework && !draft.homeworkEnabled,
  };
}

function toLocalDateTimeInput(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
